type Overrides = Record<string, boolean | string>;

// Resolves a dotted `path` within `data`. An empty path or `_self` resolves to
// `data` itself; any missing segment short-circuits to undefined.
export function resolveRaw(data: any, path: string): unknown {
  if (!path || path === '_self') {
    return data;
  }
  const segments = path.split('.');
  let current: any = data;
  for (const seg of segments) {
    if (current == null) {
      return undefined;
    }
    current = current[seg];
  }
  return current;
}

// Truthiness for an `include_if` path. An empty array counts as absent (same as
// an omitted field), so array-gated sections don't render empty.
function includeIfMet(data: any, path: string): boolean {
  const value = resolveRaw(data, path);
  return Array.isArray(value) ? value.length > 0 : !!value;
}

// Whether a block should be rendered, accounting for sidebar overrides and the
// block's `include_if` / `variant_path` conditions.
export function isBlockIncluded(
  block: any,
  data: any,
  overrides?: Overrides
): boolean {
  const override = overrides?.[block.id];
  if (override !== undefined) {
    if (block.variant_path) {
      return typeof override === 'string' && override !== 'none';
    }
    return !!override;
  }
  if (block.include_if) {
    return includeIfMet(data, block.include_if);
  }
  if (block.variant_path) {
    const variant = resolveRaw(data, block.variant_path);
    return (
      typeof variant === 'string' &&
      variant !== 'none' &&
      !!block.options?.[variant]
    );
  }
  return true;
}

// The content nodes for a block, selecting the active variant when applicable
// and ensuring every node carries a stable `_key`.
export function getBlockContent(
  block: any,
  data: any,
  overrides?: Overrides
): any[] {
  let raw: any[];
  if (block.variant_path) {
    const override = overrides?.[block.id];
    const variant =
      typeof override === 'string'
        ? override
        : (resolveRaw(data, block.variant_path) as string);
    raw = block.options?.[variant]?.content ?? [];
  } else {
    raw = block.content ?? [];
  }
  return raw
    .filter(
      (node: any) => !node.include_if || includeIfMet(data, node.include_if)
    )
    .map((node: any, i: number) =>
      node._key ? node : { ...node, _key: `${block.id ?? 'b'}-${i}` }
    );
}
